import { RoleType } from '@/services/authentication/hooks/user';
import { BasicBetterBest } from '@/services/catalogue/useBasicBetterBest';
import { useCatalogueService } from '@/services/catalogue/useCatalogueService';
import { Value } from '@udecode/plate-common';
import { BaseMargin, Category, ImageDetails, Price, Product, PropertyBag } from '../contracts/catalog/types';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';
import { useCategories } from './catalog';

interface UpdateProductArgs extends CreateProductArgs {
  id: string;
  code: string;
}

export const useUpdateProduct = () => {
  return useAPIMutation(
    (client, query) =>
      async ({
        id,
        name,
        code,
        productId,
        categoryId,
        manufacturerId,
        imageUri,
        propertyBag,
        description,
        regionId
      }: UpdateProductArgs) => {
        const response = await client.catalog.products.updateProduct({
          params: { productId: id },
          body: {
            name,
            code,
            productId,
            categoryId,
            manufacturerId,
            imageUri,
            propertyBag,
            description,
            marginExcessWarning: true,
            regionId
          }
        });
        if (response.status !== 200) {
          throw new Error('Failed to create product');
        }
        await query.invalidateQueries(['getProducts']);
        await query.invalidateQueries(['getProduct', id]);
        return response.body;
      }
  );
};
interface DeleteProductArgs {
  id: string;
}

export const useDeleteProduct = () => {
  return useAPIMutation((client, query) => async ({ id }: DeleteProductArgs) => {
    const response = await client.catalog.products.deleteProduct({ params: { productId: id } });
    if (response.status !== 200) {
      throw new Error('Failed to delete product');
    }
    await query.invalidateQueries(['getProducts']);
    await query.invalidateQueries(['getProduct', id]);
    return response.body;
  });
};

export const useProductReplacements = () => {
  return useAPIQuery(['getProductReplacements'], async client => {
    const response = await client.catalog.products.getProductReplacements();
    if (response.status !== 200) throw new Error('There is an error');
    return response.body.items;
  });
};
interface ReplaceProductArgs {
  replacedProductId: string;
  replacementProductId: string;
  effectiveFrom: Date;
}

export const useReplaceProduct = () => {
  return useAPIMutation(
    (client, query) =>
      async ({ replacedProductId, replacementProductId, effectiveFrom }: ReplaceProductArgs) => {
        const response = await client.catalog.products.replaceProduct({
          body: {
            replacedProductId,
            replacementProductId,
            effectiveDate: {
              value: effectiveFrom
            }
          }
        });
        if (response.status !== 200) {
          throw new Error('Failed to replace product');
        }
        await query.invalidateQueries(['getProducts']);
        await query.invalidateQueries(['getProduct', replacedProductId]);
        await query.invalidateQueries(['getProductReplacements']);
        return response.body;
      }
  );
};
export interface CreateProductArgs {
  description?: {
    value: Value;
  };
  propertyBag: {
    properties: PropertyBag;
    basicBetterBest?: BasicBetterBest;
  };
  imageUri?: ImageDetails;
  manufacturerId: string;
  categoryId: string;
  productId: string;
  name: string;
  regionId: string;
}
interface CreateProductWithPriceArgs extends CreateProductArgs {
  price: number;
  modifier: number;
  min: number;
  max: number;
}

export const useCreateProduct = () => {
  return useAPIMutation(
    (client, query) =>
      async ({
        name,
        productId,
        categoryId,
        manufacturerId,
        imageUri,
        propertyBag,
        description,
        price,
        modifier,
        min,
        max,
        regionId
      }: CreateProductWithPriceArgs) => {
        const codes = await client.catalog.products.getCodesForNames({
          body: {
            names: [productId]
          }
        });

        if (codes.status !== 200) {
          throw new Error('Failed to create product');
        }

        const [code] = codes.body.items;

        const response = await client.catalog.products.createProduct({
          body: {
            name,
            productId,
            categoryId,
            manufacturerId,
            imageUri,
            propertyBag,
            code: code.code,
            description,
            marginExcessWarning: true,
            regionId
          }
        });

        if (response.status !== 200) {
          throw new Error('Failed to create product');
        }

        const priceResponse = await client.catalog.products.addPrice({
          body: {
            daasCatalogItemId: response.body.id,
            price,
            effectiveDate: {
              value: new Date()
            }
          }
        });

        if (priceResponse.status !== 200) {
          throw new Error('Failed to create product');
        }

        const marginResponse = await client.catalog.products.addBaseMargin({
          body: {
            daasCatalogItemId: response.body.id,
            modifier,
            min,
            max,
            effectiveDate: {
              value: new Date()
            }
          }
        });

        if (marginResponse.status !== 200) {
          throw new Error('Failed to create product');
        }

        await query.invalidateQueries(['getProducts']);
        return response.body;
      }
  );
};

type BulkCreateProductArg = CreateProductArgs & { baseMargin: BaseMargin; catalogPrice: Price };
interface BulkCreateProductArgs {
  products: BulkCreateProductArg[];
}

export const useBulkCreateProducts = () => {
  return useAPIMutation((client, query) => async ({ products }: BulkCreateProductArgs) => {
    const codes = await client.catalog.products.getCodesForNames({
      body: {
        names: products.map(product => `${product.productId}-${product.regionId}`)
      }
    });

    if (codes.status !== 200) {
      throw new Error('Failed to create products');
    }
    const body = products.map(product => {
      const code = codes.body.items.find(code => code.name === `${product.productId}-${product.regionId}`)?.code;
      if (!code) {
        throw new Error('Failed to create products');
      }
      return {
        ...product,
        code,
        marginExcessWarning: true
      };
    });
    const response = await client.catalog.products.bulkCreateProducts({
      body
    });

    if (response.status !== 200) {
      throw new Error('Failed to create products');
    }
    await query.invalidateQueries(['getProducts']);
    return response.body;
  });
};

export const useBulkUpdateProducts = () => {
  return useAPIMutation((client, query) => async ({ products }: BulkCreateProductArgs) => {
    const body = products.map(product => {
      return {
        ...product,
        marginExcessWarning: true
      };
    });
    const response = await client.catalog.products.bulkCreateProducts({
      body
    });

    if (response.status !== 200) {
      throw new Error('Failed to create products');
    }
    await query.invalidateQueries(['getProducts']);
    return response.body;
  });
};

export const useProducts = (effectiveDate?: Date) => {
  const [configuration, showLatest] = useCatalogueService(state => [state.configuration, state.showLatest]);

  return useAPIQuery(['getProducts', configuration, showLatest, effectiveDate], async client => {
    let products: Product[] = [];

    if (!configuration) throw new Error('Invalid configuration');

    if (configuration.for === RoleType.Admin) {
      const response = await client.catalog.products.getDaaSProducts();
      if (response.status !== 200) throw new Error('There is an error');

      products = response.body.items;
    }

    if (configuration.for === RoleType.Partner) {
      const response = await client.catalog.products.getMSPProducts({ params: { partnerId: configuration.partnerId } });
      if (response.status !== 200) throw new Error('There is an error');
      products = response.body.items;
    }

    if (configuration.for === RoleType.Company) {
      const date = effectiveDate ?? (showLatest || configuration.as === RoleType.Company ? undefined : new Date());

      const response = await client.catalog.products.getCustomerProducts({
        query: { date },
        params: { companyId: configuration.companyId, partnerId: configuration.partnerId }
      });
      if (response.status !== 200) throw new Error('There is an error');
      products = response.body.items;
    }

    return products.sort((a, b) => a.name.localeCompare(b.name));
  });
};

export const useProductsByCategory = (categoryId?: string) => {
  const [items, response] = useProducts();

  return [!categoryId ? items : items.filter(item => item.category.id === categoryId), response] as const;
};

export type GroupedProducts = {
  [categoryName: string]: Product[];
};

export const groupProductsByCategories = (products: Product[], categories: Category[]) => {
  const groupedItems: GroupedProducts = {};

  // Iterate over the categoryArray to maintain the order
  categories.forEach(category => {
    const productList: Product[] = products.filter(p => p.category.name === category.name);

    if (productList.length > 0) {
      groupedItems[category.name] = productList;
    }
  });

  return groupedItems;
};

export const useProductsGroupedUpByCategory = (effectiveDate?: Date) => {
  const [items] = useProducts(effectiveDate);
  const [categories] = useCategories();

  return groupProductsByCategories(items, categories);
};

export type GroupedManufacturer = {
  id: string;
  name: string;
  items: Product[];
};

export type GroupedProductsByManufacturer = {
  [categoryName: string]: GroupedManufacturer[];
};

export const groupGroupedProductsByManufacturer = (group: GroupedProducts, accessors: string[]) => {
  const result: GroupedProductsByManufacturer = {};

  accessors.map(accessor => {
    const groupByManufacturer: { [key: string]: Product[] } = {};

    group[accessor].forEach(device => {
      const manufacturerId = device.manufacturer.id;
      if (!groupByManufacturer[manufacturerId]) {
        groupByManufacturer[manufacturerId] = [];
      }
      groupByManufacturer[manufacturerId].push(device);
    });

    result[accessor] = Object.keys(groupByManufacturer).map(manufacturerId => ({
      id: manufacturerId,
      name: groupByManufacturer[manufacturerId][0].manufacturer.name,
      items: groupByManufacturer[manufacturerId]
    }));
  });

  return result;
};

export const useProduct = (productId: string) => {
  const [configuration, showLatest] = useCatalogueService(state => [state.configuration, state.showLatest]);

  return useAPIQuery(['getProduct', productId], async client => {
    if (configuration.for === RoleType.Admin) {
      const response = await client.catalog.products.getDaaSProduct({
        params: {
          id: productId
        }
      });
      if (response.status !== 200) throw new Error('There is an error');
      return response.body;
    }

    if (configuration.for === RoleType.Partner) {
      const response = await client.catalog.products.getMSPProduct({
        params: { partnerId: configuration.partnerId, id: productId }
      });
      if (response.status !== 200) throw new Error('There is an error');
      return response.body;
    }

    if (configuration.for === RoleType.Company) {
      const response = await client.catalog.products.getCustomerProduct({
        query: { date: showLatest || configuration.as === RoleType.Company ? undefined : new Date() },
        params: { companyId: configuration.companyId, partnerId: configuration.partnerId, id: productId }
      });
      if (response.status !== 200) throw new Error('There is an error');
      return response.body;
    }

    throw new Error('Invalid configuration');
  });
};

export const useProductsByIds = (productIds: string[]) => {
  const [products] = useProducts();

  return products.filter(product => productIds.includes(product.id));
};

export const useCatalogDiff = (companyId: string, partnerId: string) => {
  return useAPIQuery(['getCustomerCatalogDiff'], async client => {
    const response = await client.catalog.products.getCustomerCatalogDiff({
      params: { companyId: companyId, partnerId: partnerId }
    });
    if (response.status !== 200) throw new Error('There is an error');
    return response.body;
  });
};

export const useProductPriceHistory = (productId: string) => {
  const [configuration, showLatest] = useCatalogueService(state => [state.configuration, state.showLatest]);

  return useAPIQuery(['getProductPriceHistory', productId, configuration, showLatest], async client => {
    if (configuration.for === RoleType.Admin) {
      const response = await client.catalog.products.getDaaSPriceHistory({ params: { productId } });
      if (response.status !== 200) throw new Error('There is an error');
      return response.body.priceHistory;
    }
    if (configuration.for === RoleType.Partner) {
      const response = await client.catalog.products.getMSPPriceHistory({
        params: { productId, partnerId: configuration.partnerId }
      });
      if (response.status !== 200) throw new Error('There is an error');
      return response.body.priceHistory;
    }
    if (configuration.for === RoleType.Company) {
      const response = await client.catalog.products.getCustomerPriceHistory({
        query: { date: showLatest ? undefined : new Date() },
        params: { productId, companyId: configuration.companyId, partnerId: configuration.partnerId }
      });
      if (response.status !== 200) throw new Error('There is an error');
      return response.body.priceHistory;
    }

    throw new Error('Invalid configuration');
  });
};
